import {
    TileType,
    PlayerState,
    MOVE_INTERVAL,
    TILE_SIZE,
    PLAYER_HEIGHT_STANDING,
    PLAYER_HEIGHT_CROUCHING,
    UNDERGROUND_ROOM_X1,
    UNDERGROUND_ROOM_Y1,
    HERO_SPAWN_REL_X,
    HERO_SPAWN_REL_Y,
    HERO_MIN_REL_X,
    HERO_MAX_REL_X,
    JUMP_HEIGHT,
} from '@blockworld/shared';
import {
    tryMoveHorizontal,
    tryMoveUp,
    tryMoveDown,
    isOnGround,
    isPassable,
    type PlayerPhysicsState,
} from '@blockworld/shared';
import Phaser from 'phaser';
import { SpeechBubble } from '../ui/SpeechBubble';
import { TokiPonaAudio, VOICE_PROFILES } from '../toki/TokiPonaAudio';
// ============================================================
// HERO NPC BEHAVIOR
// "jan wawa" — a frantic platforming character in the
// underground cavern. Client-only, uses local RNG.
// Obsessively jumps (always drifting positive-X toward x=23),
// launches from 2-high dirt stacks, frantically rearranges
// dirt between jumps. Never stands still.
//
// When the yellow doors open, switches to escape mode:
// clears dirt → walks right → waits for player → scripted
// jump sequence up through the ceiling.
// ============================================================

/** Minimal world access interface — satisfied by WorldData or a GameScene chunk wrapper. */
export interface HeroWorldAccess {
    getTile(x: number, y: number): TileType;
    setTile(x: number, y: number, type: TileType): void;
    /** Notify the falling block predictor that a block at (x,y) should be tracked. */
    notifyFallingBlock?(x: number, y: number): void;
    /** Check if the chunk containing (x, y) is loaded. */
    isChunkLoaded?(x: number, y: number): boolean;
}

/** Convert cavern-local 1-based X to world X. */
function relToWorldX(relX: number): number {
    return UNDERGROUND_ROOM_X1 + relX - 1;
}
/** Convert cavern-local 1-based Y to world Y. */
function relToWorldY(relY: number): number {
    return UNDERGROUND_ROOM_Y1 + relY - 1;
}

const HERO_MIN_X = relToWorldX(HERO_MIN_REL_X); // rel 19
const HERO_MAX_X = relToWorldX(HERO_MAX_REL_X); // rel 23
const HERO_SPAWN_X = relToWorldX(HERO_SPAWN_REL_X);
const HERO_SPAWN_Y = relToWorldY(HERO_SPAWN_REL_Y);
const HERO_JUMP_X = relToWorldX(23); // rightmost — favorite jump target
const HERO_FLOOR_Y = relToWorldY(20); // stone floor

// Escape sequence constants
const DOOR_CHECK_X = relToWorldX(24);  // first door column
const DOOR_CHECK_Y = relToWorldY(18);  // top tile of door
const ESCAPE_WALK_TARGET_X = relToWorldX(39); // stone platform destination
const PLAYER_TRIGGER_X = relToWorldX(35);     // player must pass this X

// Speech — hero cries out in the "ante" voice while stuck (phase 1)
const HERO_VOICE_KEY = 'ante';
const SPEECH_FIRST_DELAY_MS = 5000;  // first line after 5 seconds
const SPEECH_INTERVAL_MS = 10000;    // then every 10 seconds
const HERO_VOICE_LINES: number[][] = [
    [68, 118, 9, 52, 103, 65], // "o weka e mi tan ni"   — free me from this
    [17, 39, 44, 2, 44],       // "jan li lon ala lon"    — is anyone there?
    [52, 24, 2, 105],          // "mi ken ala tawa"       — I can't move
    [68, 32, 9, 52],           // "o kute e mi"           — hear me
    [68, 84, 9, 52],           // "o pona e mi"           — help me
    [52, 44, 89],              // "mi lon seme"           — where am I?
    [68, 71, 9, 47],           // "o open e lupa"         — open the door
    [79, 104, 39, 44],         // "pimeja taso li lon"    — only darkness exists
    [52, 104, 39, 44],         // "mi taso li lon"        — only I exist
    [89, 39, 22],              // "seme li kama"          — what is happening?
];

type EscapePhase = 'none' | 'clear_dirt' | 'walk' | 'wait' | 'scripted';

interface ScriptedStep {
    dx: number;
    dy: number;
    state: PlayerState;
    facing: number;
    isCrouching: boolean;
}

const ESCAPE_SCRIPT: ScriptedStep[] = [
    // 1: jumpsquat
    { dx: 0, dy: 0, state: PlayerState.JUMPSQUAT, facing: 1, isCrouching: true },
    // 2: rising, up 1
    { dx: 0, dy: -1, state: PlayerState.RISING, facing: 1, isCrouching: false },
    // 3: rising, up 1, right 1
    { dx: 1, dy: -1, state: PlayerState.RISING, facing: 1, isCrouching: false },
    // 4: rising, up 1, right 1
    { dx: 1, dy: -1, state: PlayerState.RISING, facing: 1, isCrouching: false },
    // 5-7: standing, right 1
    { dx: 1, dy: 0, state: PlayerState.IDLE, facing: 1, isCrouching: false },
    { dx: 1, dy: 0, state: PlayerState.IDLE, facing: 1, isCrouching: false },
    { dx: 1, dy: 0, state: PlayerState.IDLE, facing: 1, isCrouching: false },
    // 8: jumpsquat
    { dx: 0, dy: 0, state: PlayerState.JUMPSQUAT, facing: 1, isCrouching: true },
    // 9: rising, up 1
    { dx: 0, dy: -1, state: PlayerState.RISING, facing: 1, isCrouching: false },
    // 10: rising, up 1, right 1
    { dx: 1, dy: -1, state: PlayerState.RISING, facing: 1, isCrouching: false },
    // 11: rising, up 1, right 1
    { dx: 1, dy: -1, state: PlayerState.RISING, facing: 1, isCrouching: false },
    // 12: standing, right 1
    { dx: 1, dy: 0, state: PlayerState.IDLE, facing: 1, isCrouching: false },
    // 13: turn facing left
    { dx: 0, dy: 0, state: PlayerState.IDLE, facing: -1, isCrouching: false },
    // 14: crouch (facing left)
    { dx: 0, dy: 0, state: PlayerState.IDLE, facing: -1, isCrouching: true },
    // 15: stand (facing left)
    { dx: 0, dy: 0, state: PlayerState.IDLE, facing: -1, isCrouching: false },
    // 16: right 1 (facing right)
    { dx: 1, dy: 0, state: PlayerState.WALKING, facing: 1, isCrouching: false },
    // 17: right 1
    { dx: 1, dy: 0, state: PlayerState.WALKING, facing: 1, isCrouching: false },
    // 18-30: up 1 tile at a time (13 steps), then disappear
    { dx: 0, dy: -1, state: PlayerState.RISING, facing: 1, isCrouching: false },
    { dx: 0, dy: -1, state: PlayerState.RISING, facing: 1, isCrouching: false },
    { dx: 0, dy: -1, state: PlayerState.RISING, facing: 1, isCrouching: false },
    { dx: 0, dy: -1, state: PlayerState.RISING, facing: 1, isCrouching: false },
    { dx: 0, dy: -1, state: PlayerState.RISING, facing: 1, isCrouching: false },
    { dx: 0, dy: -1, state: PlayerState.RISING, facing: 1, isCrouching: false },
    { dx: 0, dy: -1, state: PlayerState.RISING, facing: 1, isCrouching: false },
    { dx: 0, dy: -1, state: PlayerState.RISING, facing: 1, isCrouching: false },
    { dx: 0, dy: -1, state: PlayerState.RISING, facing: 1, isCrouching: false },
    { dx: 0, dy: -1, state: PlayerState.RISING, facing: 1, isCrouching: false },
    { dx: 0, dy: -1, state: PlayerState.RISING, facing: 1, isCrouching: false },
    { dx: 0, dy: -1, state: PlayerState.RISING, facing: 1, isCrouching: false },
    { dx: 0, dy: -1, state: PlayerState.RISING, facing: 1, isCrouching: false },
];

type HeroGoal =
    | { type: 'none' }
    | { type: 'walk_to'; targetX: number }
    | { type: 'pickup'; blockX: number; blockY: number; shortHop: boolean; phase: 'approach' | 'hop' | 'grab' }
    | { type: 'place'; targetX: number; targetY: number; phase: 'approach' | 'place' };

export class HeroBehavior {
    // Public state read by renderer
    x: number;
    y: number;
    facing = 1;
    isCrouching = false;
    isHoldingBlock = false;
    heldBlockType: TileType | null = null;
    state: PlayerState = PlayerState.FALLING;
    jumpRemaining = 0;
    /** Set true when escape sequence completes — renderer should hide the hero. */
    destroyed = false;
    /** Set true after boots are dropped — renderer should use bootless sprites. */
    droppedBoots = false;
    /** Updated by GameScene each frame so hero knows where the player is. */
    playerX = 0;

    private world: HeroWorldAccess;
    private lastTick = 0;
    private goal: HeroGoal = { type: 'none' };
    private pendingJumpHeight = JUMP_HEIGHT;

    // Jump direction stored separately so goal can be cleared immediately
    private jumpDir = 0;
    // Track whether we've jumped from the current 2-stack (allows dismantle after)
    private jumpedFrom2StackX: number | null = null;
    // Consecutive jumps at x=23 — walk away after 2
    private jumpAtFavCount = 0;
    // Set on landing — forces a block pickup before the next jump (frantic)
    private justLandedFromJump = false;
    // Tracks last jump origin to prevent repeat in-place jumps
    private lastJumpX: number | null = null;

    // Escape sequence state
    private doorOpened = false;
    private escapePhase: EscapePhase = 'none';
    private scriptTick = 0;

    // Speech state
    private scene: Phaser.Scene;
    private audio: TokiPonaAudio;
    private activeBubble: SpeechBubble | null = null;
    private lastSpeechTime = 0;
    private hasSpokenOnce = false;

    // PlayerPhysicsState adapter (mutated in place by physics functions)
    private physicsState: PlayerPhysicsState;

    constructor(world: HeroWorldAccess, scene: Phaser.Scene, audio: TokiPonaAudio) {
        this.world = world;
        this.scene = scene;
        this.audio = audio;
        this.x = HERO_SPAWN_X;
        this.y = HERO_SPAWN_Y;

        this.physicsState = {
            x: this.x,
            y: this.y,
            state: this.state,
            facing: this.facing,
            isCrouching: this.isCrouching,
            isHoldingBlock: this.isHoldingBlock,
            heldBlockType: this.heldBlockType,
            jumpRemaining: this.jumpRemaining,
            jumpsquatStartTime: 0,
        };

        this.lastTick = Math.floor(Date.now() / MOVE_INTERVAL);
    }

    /** Call each frame. Processes ticks on 100ms boundaries. */
    update(now: number): void {
        const currentTick = Math.floor(now / MOVE_INTERVAL);
        while (this.lastTick < currentTick) {
            this.lastTick++;
            this.tick();
        }

        // Phase 1 speech: cry out every 15s while stuck in the room
        if (!this.doorOpened && !this.destroyed) {
            if (this.lastSpeechTime === 0) {
                this.lastSpeechTime = now;
            }
            const delay = this.hasSpokenOnce ? SPEECH_INTERVAL_MS : SPEECH_FIRST_DELAY_MS;
            if (now - this.lastSpeechTime >= delay) {
                this.hasSpokenOnce = true;
                this.lastSpeechTime = now;
                const line = HERO_VOICE_LINES[Math.floor(Math.random() * HERO_VOICE_LINES.length)];
                this.createBubble(line);
            }
        }
    }

    /** Update speech bubble screen position — call from render loop. */
    updateBubblePosition(offsetX: number, offsetY: number): void {
        if (this.activeBubble && this.activeBubble.isActive()) {
            const screenX = this.x * TILE_SIZE + offsetX;
            const screenY = this.y * TILE_SIZE + offsetY;
            this.activeBubble.updatePosition(screenX, screenY);
        } else if (this.activeBubble) {
            this.activeBubble = null;
        }
    }

    destroy(): void {
        this.goal = { type: 'none' };
        if (this.activeBubble) {
            this.activeBubble.destroy();
            this.activeBubble = null;
        }
    }

    // ============================================================
    // TICK — one step of physics + AI per 100ms
    // ============================================================

    private tick(): void {
        if (this.destroyed) return;

        // Guard: skip tick until the hero's chunks are loaded.
        // Without this, getTile returns SKY for unloaded chunks, causing the
        // hero to think the yellow door is open and immediately self-destruct.
        const heroChunkLoaded = this.world.isChunkLoaded?.(this.x, HERO_FLOOR_Y) ?? true;
        if (!heroChunkLoaded) return;

        // Check for door opening (poll the first door tile each tick)
        // Only trust the door tile if its chunk is loaded — unloaded chunks
        // return SKY which would falsely trigger escape mode.
        const doorChunkLoaded = this.world.isChunkLoaded?.(DOOR_CHECK_X, DOOR_CHECK_Y) ?? true;
        if (!this.doorOpened && doorChunkLoaded && this.world.getTile(DOOR_CHECK_X, DOOR_CHECK_Y) !== TileType.YELLOW_DOOR) {
            this.doorOpened = true;
            this.escapePhase = 'clear_dirt';
            // Stop any active speech bubble when escape begins
            if (this.activeBubble) {
                this.activeBubble.destroy();
                this.activeBubble = null;
            }
        }

        // Escape mode overrides normal behavior entirely (no physics)
        if (this.doorOpened) {
            this.escapeTick();
            return;
        }

        this.syncToPhysics();

        // 1. Physics: gravity & jump continuation
        switch (this.state) {
            case PlayerState.FALLING:
                this.applyGravity();
                break;
            case PlayerState.RISING:
                this.applyRise();
                break;
            case PlayerState.JUMPSQUAT:
                this.state = PlayerState.RISING;
                this.jumpRemaining = this.pendingJumpHeight;
                this.isCrouching = false;
                break;
            default:
                if (!isOnGround(this.physicsState, this.world as any)) {
                    this.state = PlayerState.FALLING;
                    this.syncFromPhysics();
                    this.applyGravity();
                    return;
                }
                break;
        }

        // 2. AI: process current goal or pick a new one (never idle)
        if (this.state === PlayerState.IDLE || this.state === PlayerState.WALKING) {
            this.processGoal();
        }

        this.syncFromPhysics();
        // Enforce rightward facing during positive-X air drift
        // (syncFromPhysics can stomp on the facing=1 set by startJump)
        // Note: processGoal() above can mutate state to JUMPSQUAT via startJump(),
        // but TS narrows it after the IDLE/WALKING guard — use a local to bypass.
        const currentState = this.state as PlayerState;
        if (this.jumpDir > 0 && (currentState === PlayerState.RISING || currentState === PlayerState.FALLING || currentState === PlayerState.JUMPSQUAT)) {
            this.facing = 1;
        }
        this.clampBounds();
    }

    // ============================================================
    // PHYSICS
    // ============================================================

    private applyGravity(): void {
        this.syncToPhysics();
        const moved = tryMoveDown(this.physicsState, this.world as any);
        this.syncFromPhysics();

        if (!moved || isOnGround(this.physicsState, this.world as any)) {
            this.state = PlayerState.IDLE;
            this.justLandedFromJump = true;
            this.syncFromPhysics();
        }
    }

    private applyRise(): void {
        if (this.jumpRemaining <= 0) {
            this.state = PlayerState.FALLING;
            return;
        }

        this.syncToPhysics();
        const moved = tryMoveUp(this.physicsState, this.world as any);
        this.syncFromPhysics();

        if (moved) {
            this.jumpRemaining--;
            // Horizontal drift during jump — always positive X
            if (this.jumpDir > 0) {
                this.syncToPhysics();
                tryMoveHorizontal(this.physicsState, this.world as any, this.jumpDir);
                this.syncFromPhysics();
            }
            // During short-hop pickup, grab at peak
            if (this.goal.type === 'pickup' && this.goal.phase === 'hop' && this.jumpRemaining <= 0) {
                this.tryGrabBlock();
            }
        } else {
            this.state = PlayerState.FALLING;
        }
    }

    // ============================================================
    // AI GOAL PROCESSING
    // ============================================================

    private processGoal(): void {
        switch (this.goal.type) {
            case 'none':
                this.pickNewGoal();
                break;
            case 'walk_to':
                this.processWalkTo();
                break;
            case 'pickup':
                this.processPickup();
                break;
            case 'place':
                this.processPlace();
                break;
        }
    }

    private processWalkTo(): void {
        if (this.goal.type !== 'walk_to') return;
        const { targetX } = this.goal;

        if (this.x === targetX) {
            this.goal = { type: 'none' };
            this.state = PlayerState.IDLE;
            return;
        }

        const dir = targetX > this.x ? 1 : -1;
        this.facing = dir;
        this.state = PlayerState.WALKING;
        this.syncToPhysics();
        const moved = tryMoveHorizontal(this.physicsState, this.world as any, dir);
        this.syncFromPhysics();

        if (!moved) {
            this.goal = { type: 'none' };
            this.state = PlayerState.IDLE;
        }
    }

    private processPickup(): void {
        if (this.goal.type !== 'pickup') return;
        const { blockX, shortHop, phase } = this.goal;

        if (phase === 'approach') {
            const desiredX = blockX + (this.x <= blockX ? -1 : 1);
            const clampedX = Math.max(HERO_MIN_X, Math.min(HERO_MAX_X, desiredX));

            if (this.x === clampedX || Math.abs(this.x - blockX) <= 1) {
                // Face toward the block
                this.facing = blockX > this.x ? 1 : blockX < this.x ? -1 : this.facing;
                if (shortHop) {
                    this.goal = { ...this.goal, phase: 'hop' };
                    this.startJump(1, 0);
                } else {
                    this.goal = { ...this.goal, phase: 'grab' };
                    this.tryGrabBlock();
                }
            } else {
                const dir = clampedX > this.x ? 1 : -1;
                this.facing = dir;
                this.syncToPhysics();
                const moved = tryMoveHorizontal(this.physicsState, this.world as any, dir);
                this.syncFromPhysics();
                if (!moved) {
                    this.goal = { type: 'none' };
                }
            }
        } else if (phase === 'grab') {
            this.tryGrabBlock();
        }
    }

    private tryGrabBlock(): void {
        if (this.goal.type !== 'pickup') return;
        const { blockX, blockY } = this.goal;

        // Face toward the block
        if (blockX !== this.x) {
            this.facing = blockX > this.x ? 1 : -1;
        }

        if (this.world.getTile(blockX, blockY) === TileType.DIRT) {
            this.world.setTile(blockX, blockY, TileType.SKY);
            this.isHoldingBlock = true;
            this.heldBlockType = TileType.DIRT;
        }
        this.goal = { type: 'none' };
    }

    private processPlace(): void {
        if (this.goal.type !== 'place') return;
        const { targetX, phase } = this.goal;

        if (phase === 'approach') {
            const desiredX = targetX + (this.x <= targetX ? -1 : 1);
            const clampedX = Math.max(HERO_MIN_X, Math.min(HERO_MAX_X, desiredX));

            if (this.x === clampedX || Math.abs(this.x - targetX) <= 1) {
                // Face toward placement
                this.facing = targetX > this.x ? 1 : targetX < this.x ? -1 : this.facing;
                this.goal = { ...this.goal, phase: 'place' };
                this.doPlace();
            } else {
                const dir = clampedX > this.x ? 1 : -1;
                this.facing = dir;
                this.syncToPhysics();
                const moved = tryMoveHorizontal(this.physicsState, this.world as any, dir);
                this.syncFromPhysics();
                if (!moved) {
                    this.goal = { type: 'none' };
                }
            }
        } else if (phase === 'place') {
            this.doPlace();
        }
    }

    private doPlace(): void {
        if (this.goal.type !== 'place') return;
        const { targetX, targetY } = this.goal;

        if (targetX !== this.x) {
            this.facing = targetX > this.x ? 1 : -1;
        }

        if (this.isHoldingBlock && isPassable(this.world.getTile(targetX, targetY))) {
            this.world.setTile(targetX, targetY, TileType.DIRT);
            this.isHoldingBlock = false;
            this.heldBlockType = null;
        }
        this.goal = { type: 'none' };
    }

    private startJump(height: number, dir: number): void {
        this.pendingJumpHeight = height;
        // Enforce positive-X only drift (0 or 1, never -1)
        this.jumpDir = Math.max(0, dir);
        // Face the drift direction (rightward); keep current facing if straight up
        if (this.jumpDir > 0) {
            this.facing = 1;
        }
        this.isCrouching = true;
        this.state = PlayerState.JUMPSQUAT;
        // Track origin to prevent repeat in-place jumps
        this.lastJumpX = this.x;
        // Clear goal immediately — jumpDir stored separately for applyRise.
        // Pickup goals keep their goal (hop phase handled in applyRise).
        if (this.goal.type !== 'pickup') {
            this.goal = { type: 'none' };
        }
    }

    // ============================================================
    // AI DECISION MAKING — frantic jumper, never idle
    // ============================================================

    /** Would jumping from current position be a repeat of the last jump? */
    private wouldRepeatJump(): boolean {
        return this.lastJumpX === this.x;
    }

    /** Walk toward a target, clearing lastJumpX so next jump is fresh. */
    private walkToward(targetX: number): void {
        this.lastJumpX = null;
        if (targetX !== this.x) {
            this.facing = targetX > this.x ? 1 : -1;
        }
        this.goal = { type: 'walk_to', targetX };
    }

    private pickNewGoal(): void {
        const roll = Math.random();

        // Priority 1: Holding block → place it immediately (frantic)
        if (this.isHoldingBlock) {
            this.goalPlaceDirt();
            return;
        }

        // Priority 2: On top of a 2-stack → jump toward x=23 (primary loop)
        if (this.isOnTopOf2Stack()) {
            if (this.x >= HERO_MAX_X) {
                // At right wall — allow up to 2 jumps, then dismantle
                if (this.jumpAtFavCount >= 2) {
                    this.jumpAtFavCount = 0;
                    this.jumpedFrom2StackX = null;
                    const topY = HERO_FLOOR_Y - 2;
                    this.goal = {
                        type: 'pickup',
                        blockX: this.x,
                        blockY: topY,
                        shortHop: false,
                        phase: 'grab',
                    };
                    return;
                }
                this.jumpAtFavCount++;
                this.jumpedFrom2StackX = this.x;
                this.startJump(JUMP_HEIGHT, 0);
                return;
            }
            this.jumpedFrom2StackX = this.x;
            this.startJump(JUMP_HEIGHT, 1);
            return;
        }

        // Priority 2.5: Just landed — grab a block before jumping again (frantic)
        if (this.justLandedFromJump && !this.isHoldingBlock) {
            this.justLandedFromJump = false;
            // Dismantle a nearby 2-stack if one exists
            const nearStack = this.find2HighStack();
            if (nearStack && Math.abs(this.x - nearStack.x) <= 2) {
                this.facing = nearStack.x > this.x ? 1 : nearStack.x < this.x ? -1 : this.facing;
                this.goal = {
                    type: 'pickup',
                    blockX: nearStack.x,
                    blockY: nearStack.topY,
                    shortHop: Math.abs(this.x - nearStack.x) <= 1,
                    phase: 'approach',
                };
                return;
            }
            // Grab any ground-level dirt
            const nearDirt = this.findGroundDirt();
            if (nearDirt) {
                this.facing = nearDirt.x > this.x ? 1 : nearDirt.x < this.x ? -1 : this.facing;
                this.goal = {
                    type: 'pickup',
                    blockX: nearDirt.x,
                    blockY: nearDirt.y,
                    shortHop: false,
                    phase: 'approach',
                };
                return;
            }
            // Nothing to grab — fall through to normal priorities
        }
        this.justLandedFromJump = false;

        // Priority 3: At x=23 (not on 2-stack) — allow up to 2 jumps, then rearrange
        const heroRelX = this.x - UNDERGROUND_ROOM_X1 + 1;
        if (heroRelX === 23) {
            if (this.jumpAtFavCount >= 2) {
                this.jumpAtFavCount = 0;
                // Adjacent 2-stack blocking us? Dismantle it directly
                const stack = this.find2HighStack();
                if (stack && Math.abs(this.x - stack.x) <= 1) {
                    this.facing = stack.x > this.x ? 1 : stack.x < this.x ? -1 : this.facing;
                    this.goal = {
                        type: 'pickup',
                        blockX: stack.x,
                        blockY: stack.topY,
                        shortHop: true,
                        phase: 'approach',
                    };
                    return;
                }
                // Walk toward a distant stack or wander left
                if (stack) {
                    const adj = this.x < stack.x ? stack.x - 1 : stack.x + 1;
                    const clampedAdj = Math.max(HERO_MIN_X, Math.min(HERO_MAX_X, adj));
                    if (clampedAdj !== this.x) {
                        this.walkToward(clampedAdj);
                    } else {
                        this.walkToward(Math.max(HERO_MIN_X, this.x - 2));
                    }
                } else {
                    this.walkToward(HERO_MIN_X + Math.floor(Math.random() * 3));
                }
                return;
            }
            this.jumpAtFavCount++;
            this.startJump(JUMP_HEIGHT, 0);
            return;
        }

        // Reset fav counter when away from x=23
        this.jumpAtFavCount = 0;

        // Priority 4: 2-stack exists → get to it and jump from it
        const stack = this.find2HighStack();
        if (stack) {
            const distToStack = Math.abs(this.x - stack.x);

            if (this.isStandingOnDirt() && !this.wouldRepeatJump()) {
                // On dirt — jump toward the stack (positive drift)
                this.startJump(JUMP_HEIGHT, 1);
                return;
            }

            // Adjacent to stack → short jump (height 2) to land precisely on top
            // JUMP_HEIGHT=3 overshoots: tick 2 drifts to stack.x, tick 3 to stack.x+1
            // Height 2: tick 2 is the peak at stack.x, then falls onto stack
            if (distToStack === 1 && !this.wouldRepeatJump()) {
                this.startJump(2, stack.x > this.x ? 1 : 0);
                return;
            }

            // At stack.x but not on top (rare — e.g. fell from above) → jump up
            if (distToStack === 0) {
                this.startJump(JUMP_HEIGHT, 1);
                return;
            }

            // Walk to adjacent position (NOT into the stack column — blocked at head height)
            const approachX = this.x < stack.x ? stack.x - 1 : stack.x + 1;
            const clampedApproach = Math.max(HERO_MIN_X, Math.min(HERO_MAX_X, approachX));
            if (clampedApproach !== this.x) {
                this.walkToward(clampedApproach);
            } else {
                // Can't get closer — jump toward it
                this.startJump(2, stack.x >= this.x ? 1 : 0);
            }
            return;
        }

        // No 2-stack exists — clear record
        this.jumpedFrom2StackX = null;

        // Priority 5: Standing on dirt → jump toward x=23 (if not repeating)
        if (this.isStandingOnDirt() && !this.wouldRepeatJump()) {
            this.startJump(JUMP_HEIGHT, 1);
            return;
        }

        // Priority 6: Ground dirt → always pick up (frantic — never skip a block)
        const dirtBlock = this.findGroundDirt();
        if (dirtBlock) {
            if (dirtBlock.x !== this.x) {
                this.facing = dirtBlock.x > this.x ? 1 : -1;
            }
            this.goal = {
                type: 'pickup',
                blockX: dirtBlock.x,
                blockY: dirtBlock.y,
                shortHop: false,
                phase: 'approach',
            };
            return;
        }

        // Priority 7: No dirt at all — walk somewhere new, then jump
        if (this.wouldRepeatJump() || this.x >= HERO_JUMP_X) {
            // Walk left to create distance for a fresh rightward jump
            const walkTarget = Math.max(HERO_MIN_X, this.x - 1 - Math.floor(Math.random() * 2));
            this.walkToward(walkTarget);
        } else {
            this.startJump(JUMP_HEIGHT, 1);
        }
    }

    private goalPlaceDirt(): void {
        const candidates: { x: number; y: number }[] = [];
        for (let wx = HERO_MIN_X; wx <= HERO_MAX_X; wx++) {
            // Place on the floor
            const placeY = HERO_FLOOR_Y - 1;
            if (isPassable(this.world.getTile(wx, placeY)) &&
                !isPassable(this.world.getTile(wx, HERO_FLOOR_Y))) {
                candidates.push({ x: wx, y: placeY });
            }
            // Stack on existing dirt (max 2-stack)
            const stackY = HERO_FLOOR_Y - 2;
            if (this.world.getTile(wx, stackY + 1) === TileType.DIRT &&
                isPassable(this.world.getTile(wx, stackY))) {
                candidates.push({ x: wx, y: stackY });
            }
        }

        if (candidates.length > 0) {
            const target = candidates[Math.floor(Math.random() * candidates.length)];
            if (target.x !== this.x || (target.y !== this.y && target.y !== this.y + 1)) {
                // Face toward placement target
                if (target.x !== this.x) {
                    this.facing = target.x > this.x ? 1 : -1;
                }
                this.goal = { type: 'place', targetX: target.x, targetY: target.y, phase: 'approach' };
                return;
            }
        }

        // Fallback: place in front of hero — NEVER destroy blocks
        for (const tryDir of [this.facing, -this.facing]) {
            const frontX = this.x + tryDir;
            if (frontX >= HERO_MIN_X && frontX <= HERO_MAX_X) {
                const placeY = HERO_FLOOR_Y - 1;
                if (isPassable(this.world.getTile(frontX, placeY))) {
                    this.facing = tryDir;
                    this.goal = { type: 'place', targetX: frontX, targetY: placeY, phase: 'approach' };
                    return;
                }
                // Try stacking
                const stackY = HERO_FLOOR_Y - 2;
                if (this.world.getTile(frontX, placeY) === TileType.DIRT &&
                    isPassable(this.world.getTile(frontX, stackY))) {
                    this.facing = tryDir;
                    this.goal = { type: 'place', targetX: frontX, targetY: stackY, phase: 'approach' };
                    return;
                }
            }
        }

        // Last resort: place directly on the floor at hero's X if possible
        const heroFloorY = HERO_FLOOR_Y - 1;
        if (isPassable(this.world.getTile(this.x, heroFloorY))) {
            this.goal = { type: 'place', targetX: this.x, targetY: heroFloorY, phase: 'place' };
            return;
        }

        // Truly nowhere to place — put it above the hero's head (force-place at sky)
        // This should be extremely rare. Find ANY passable tile.
        for (let wx = HERO_MIN_X; wx <= HERO_MAX_X; wx++) {
            for (let wy = HERO_FLOOR_Y - 3; wy >= HERO_FLOOR_Y - 5; wy--) {
                if (isPassable(this.world.getTile(wx, wy))) {
                    this.world.setTile(wx, wy, TileType.DIRT);
                    this.isHoldingBlock = false;
                    this.heldBlockType = null;
                    return;
                }
            }
        }
    }

    // ============================================================
    // WORLD SCANNING HELPERS
    // ============================================================

    /** Find a 2-high dirt stack in the hero's range. */
    private find2HighStack(): { x: number; topY: number } | null {
        for (let wx = HERO_MIN_X; wx <= HERO_MAX_X; wx++) {
            const bottomY = HERO_FLOOR_Y - 1;
            const topY = HERO_FLOOR_Y - 2;
            if (this.world.getTile(wx, bottomY) === TileType.DIRT &&
                this.world.getTile(wx, topY) === TileType.DIRT) {
                return { x: wx, topY };
            }
        }
        return null;
    }

    /** Find a single ground-level dirt block (not part of a 2-stack). */
    private findGroundDirt(): { x: number; y: number } | null {
        const candidates: { x: number; y: number }[] = [];
        for (let wx = HERO_MIN_X; wx <= HERO_MAX_X; wx++) {
            const y = HERO_FLOOR_Y - 1;
            if (this.world.getTile(wx, y) === TileType.DIRT) {
                const aboveY = y - 1;
                if (this.world.getTile(wx, aboveY) !== TileType.DIRT) {
                    candidates.push({ x: wx, y });
                }
            }
        }
        if (candidates.length === 0) return null;
        return candidates[Math.floor(Math.random() * candidates.length)];
    }

    /** Check if hero is currently standing on any DIRT block. */
    private isStandingOnDirt(): boolean {
        const height = this.isCrouching ? PLAYER_HEIGHT_CROUCHING : PLAYER_HEIGHT_STANDING;
        const belowFeet = this.y + height;
        return this.world.getTile(this.x, belowFeet) === TileType.DIRT;
    }

    /** Check if hero is on top of a 2-high dirt stack. */
    private isOnTopOf2Stack(): boolean {
        const height = this.isCrouching ? PLAYER_HEIGHT_CROUCHING : PLAYER_HEIGHT_STANDING;
        const belowFeet = this.y + height;
        return this.world.getTile(this.x, belowFeet) === TileType.DIRT &&
               this.world.getTile(this.x, belowFeet + 1) === TileType.DIRT;
    }

    // ============================================================
    // SPEECH
    // ============================================================

    private createBubble(words: number[]): void {
        if (this.activeBubble && this.activeBubble.isActive()) {
            this.activeBubble.destroy();
        }
        const screenX = this.x * TILE_SIZE;
        const screenY = this.y * TILE_SIZE;
        this.activeBubble = new SpeechBubble({
            scene: this.scene,
            words,
            playerScreenX: screenX,
            playerScreenY: screenY,
            audio: this.audio,
            profile: VOICE_PROFILES[HERO_VOICE_KEY],
        });
    }

    // ============================================================
    // ESCAPE SEQUENCE — triggered when yellow doors open
    // ============================================================

    private escapeTick(): void {
        switch (this.escapePhase) {
            case 'clear_dirt':
                this.escapeClearDirt();
                break;
            case 'walk':
                this.escapeWalk();
                break;
            case 'wait':
                this.escapeWait();
                break;
            case 'scripted':
                this.escapeScripted();
                break;
        }
    }

    /** Phase 1: Physically deconstruct dirt at x=23, then transition to walk.
     *  Runs normal physics (gravity, jumps) with escape-specific goal selection. */
    private escapeClearDirt(): void {
        // Run normal physics first
        this.syncToPhysics();
        switch (this.state) {
            case PlayerState.FALLING:
                this.applyGravity();
                break;
            case PlayerState.RISING:
                this.applyRise();
                break;
            case PlayerState.JUMPSQUAT:
                this.state = PlayerState.RISING;
                this.jumpRemaining = this.pendingJumpHeight;
                this.isCrouching = false;
                break;
            default:
                if (!isOnGround(this.physicsState, this.world as any)) {
                    this.state = PlayerState.FALLING;
                    this.syncFromPhysics();
                    this.applyGravity();
                    return;
                }
                break;
        }

        // AI: only act when grounded
        if (this.state === PlayerState.IDLE || this.state === PlayerState.WALKING) {
            this.escapeGoalClearDirt();
        }

        this.syncFromPhysics();
        this.clampBounds();
    }

    /** Escape-specific goal picker: move dirt away from x=23 using the
     *  normal pickup/place system, then transition to 'walk'. */
    private escapeGoalClearDirt(): void {
        // Process any in-progress goal first
        if (this.goal.type !== 'none') {
            this.processGoal();
            return;
        }

        // If holding a block → place it away from x=23
        if (this.isHoldingBlock) {
            // Place at the furthest left open floor spot
            for (let wx = HERO_MIN_X; wx < HERO_MAX_X; wx++) {
                const placeY = HERO_FLOOR_Y - 1;
                if (isPassable(this.world.getTile(wx, placeY)) &&
                    !isPassable(this.world.getTile(wx, HERO_FLOOR_Y))) {
                    this.goal = { type: 'place', targetX: wx, targetY: placeY, phase: 'approach' };
                    return;
                }
            }
            // Stack on existing dirt if no floor spots
            for (let wx = HERO_MIN_X; wx < HERO_MAX_X; wx++) {
                const stackY = HERO_FLOOR_Y - 2;
                if (this.world.getTile(wx, HERO_FLOOR_Y - 1) === TileType.DIRT &&
                    isPassable(this.world.getTile(wx, stackY))) {
                    this.goal = { type: 'place', targetX: wx, targetY: stackY, phase: 'approach' };
                    return;
                }
            }
            // Fallback: drop in front
            const frontX = Math.max(HERO_MIN_X, this.x - 1);
            const dropY = HERO_FLOOR_Y - 1;
            if (isPassable(this.world.getTile(frontX, dropY))) {
                this.goal = { type: 'place', targetX: frontX, targetY: dropY, phase: 'approach' };
            } else {
                // Force-drop held block
                this.isHoldingBlock = false;
                this.heldBlockType = null;
            }
            return;
        }

        // Check for dirt at x=23 (scan top-down)
        for (let dy = 3; dy >= 1; dy--) {
            const checkY = HERO_FLOOR_Y - dy;
            if (this.world.getTile(HERO_MAX_X, checkY) === TileType.DIRT) {
                // There's a dirt block — determine pickup method
                const isTopOf2Stack = dy >= 2 &&
                    this.world.getTile(HERO_MAX_X, checkY + 1) === TileType.DIRT;

                // Need to stand adjacent (one tile left of x=23)
                const approachX = HERO_MAX_X - 1;

                // If on top of the pile at x=23, walk off first
                if (this.x === HERO_MAX_X && this.y < HERO_FLOOR_Y - PLAYER_HEIGHT_STANDING) {
                    this.walkToward(approachX);
                    return;
                }

                // Top of a 2-stack: need short-hop pickup
                if (isTopOf2Stack) {
                    this.goal = {
                        type: 'pickup',
                        blockX: HERO_MAX_X,
                        blockY: checkY,
                        shortHop: true,
                        phase: 'approach',
                    };
                } else {
                    // Ground-level dirt: direct pickup
                    this.goal = {
                        type: 'pickup',
                        blockX: HERO_MAX_X,
                        blockY: checkY,
                        shortHop: false,
                        phase: 'approach',
                    };
                }
                return;
            }
        }

        // x=23 is clear — ensure hero is standing on the floor at x=23
        const standY = HERO_FLOOR_Y - PLAYER_HEIGHT_STANDING;
        if (this.x !== HERO_MAX_X) {
            this.walkToward(HERO_MAX_X);
            return;
        }

        // Ready — snap to floor and transition
        this.y = standY;
        this.state = PlayerState.IDLE;
        this.isCrouching = false;
        this.facing = 1;
        this.escapePhase = 'walk';
    }

    /** Phase 2: Walk right 1 tile per tick to the stone platform at rel x=39. */
    private escapeWalk(): void {
        if (this.x >= ESCAPE_WALK_TARGET_X) {
            this.x = ESCAPE_WALK_TARGET_X;
            this.y = HERO_FLOOR_Y - PLAYER_HEIGHT_STANDING;
            this.state = PlayerState.IDLE;
            this.escapePhase = 'wait';
            return;
        }

        this.facing = 1;
        this.state = PlayerState.WALKING;
        this.x += 1;
    }

    /** Phase 3: Wait at x=39 until the player is past x=35. */
    private escapeWait(): void {
        this.state = PlayerState.IDLE;
        this.y = HERO_FLOOR_Y - PLAYER_HEIGHT_STANDING;

        if (this.playerX > PLAYER_TRIGGER_X) {
            this.facing = 1;
            this.escapePhase = 'scripted';
            this.scriptTick = 0;
        } else {
            // Face toward player (left) while waiting
            this.facing = -1;
        }
    }

    /** Phase 4: Execute the 30-step scripted jump sequence, then disappear. */
    private escapeScripted(): void {
        if (this.scriptTick >= ESCAPE_SCRIPT.length) {
            this.destroyed = true;
            return;
        }

        const step = ESCAPE_SCRIPT[this.scriptTick];
        this.x += step.dx;
        this.y += step.dy;
        this.state = step.state;
        this.facing = step.facing;
        this.isCrouching = step.isCrouching;
        this.scriptTick++;

        // Frame 14 (idx 13): hero crouches — drop boots 1 tile to the left at feet level
        if (this.scriptTick === 14) {
            this.droppedBoots = true;
            const bootsX = this.x - 1;
            const height = this.isCrouching ? PLAYER_HEIGHT_CROUCHING : PLAYER_HEIGHT_STANDING;
            const bootsY = this.y + height - 1; // feet level
            if (isPassable(this.world.getTile(bootsX, bootsY))) {
                this.world.setTile(bootsX, bootsY, TileType.BOOTS);
                this.world.notifyFallingBlock?.(bootsX, bootsY);
                if (this.scene.cache.audio.exists('equip_boots')) {
                    this.scene.sound.play('equip_boots', { volume: 0.35 });
                }
            }
        }
    }

    // ============================================================
    // STATE SYNC
    // ============================================================

    private syncToPhysics(): void {
        this.physicsState.x = this.x;
        this.physicsState.y = this.y;
        this.physicsState.state = this.state;
        this.physicsState.facing = this.facing;
        this.physicsState.isCrouching = this.isCrouching;
        this.physicsState.isHoldingBlock = this.isHoldingBlock;
        this.physicsState.heldBlockType = this.heldBlockType;
        this.physicsState.jumpRemaining = this.jumpRemaining;
    }

    private syncFromPhysics(): void {
        this.x = this.physicsState.x;
        this.y = this.physicsState.y;
        this.facing = this.physicsState.facing;
    }

    private clampBounds(): void {
        if (this.x < HERO_MIN_X) this.x = HERO_MIN_X;
        if (this.x > HERO_MAX_X) this.x = HERO_MAX_X;
    }
}
